import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check } from "@phosphor-icons/react";

// Searchable client picker. Filters by name/email/phone and renders only top matches for speed.
export default function ClientCombobox({ clients = [], value, onChange, disabled, testid = "client-select" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = clients.find((c) => c.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? clients.filter(
          (c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.email || "").toLowerCase().includes(q) ||
            (c.phone || "").replace(/\D/g, "").includes(q.replace(/\D/g, "")) && q.replace(/\D/g, "") !== ""
        )
      : clients;
    return base.slice(0, 50);
  }, [clients, query]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" disabled={disabled}
          data-testid={testid}
          className="mt-1.5 w-full justify-between font-normal"
        >
          <span className={selected ? "truncate" : "text-muted-foreground"}>
            {selected ? selected.name : "Select client"}
          </span>
          <CaretUpDown size={16} className="opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email, phone…"
            value={query} onValueChange={setQuery}
            data-testid="client-search-input"
          />
          <CommandList>
            <CommandEmpty>No client found.</CommandEmpty>
            {filtered.map((c) => (
              <CommandItem
                key={c.id} value={c.id}
                onSelect={() => { onChange(c.id); setOpen(false); setQuery(""); }}
                data-testid={`client-option-${c.id}`}
              >
                <Check size={16} className={`mr-2 shrink-0 ${value === c.id ? "opacity-100 text-[#00B4D8]" : "opacity-0"}`} />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm truncate">{c.name}</span>
                  {(c.email || c.phone) && <span className="text-xs text-muted-foreground truncate">{c.email || c.phone}</span>}
                </div>
              </CommandItem>
            ))}
            {query.trim() === "" && clients.length > 50 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Showing first 50 — type to search all {clients.length}</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
